/**
 * FinanceConnectionRow — one connected finance source (bank account /
 * accounting org / CRM env / fiscal mailbox) rendered as a uniform list
 * row.
 *
 * Thin wrapper around :class:`ListItemRow` (`@ando/ui`) — same chrome
 * as the inbox / documents / work rows so the operator scans the
 * Finance hub with the same eye pattern they use elsewhere.
 *
 * Slot wiring
 * -----------
 *   - avatar   → :class:`SenderAvatar` with the capability-specific
 *                colour from :data:`FINANCE_PROVIDER_COLORS` AND the
 *                lucide glyph that matches the capability semantics
 *                (Building2 for banking, Receipt for accounting,
 *                Briefcase for CRM, Mail for fiscal).
 *   - eyebrow  → CAPABILITY · provider slug (uppercase 9px).
 *   - title    → ``account_label`` (typically the IBAN tail / FiC
 *                company / Salesforce org alias / mailbox address).
 *   - trailing → days-until-consent-expiry chip (only for PSD2 + AdE
 *                delega rows; null for OAuth-refresh capabilities).
 *   - pills    → optional ``needs_reauth`` warning + status pill.
 */

import {
  Briefcase,
  Building2,
  Mail,
  Receipt,
  Shield,
  type LucideIcon,
} from "lucide-react";
import type * as React from "react";
import { msg } from "@lingui/core/macro";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";

import {
  ListItemEyebrow,
  ListItemRow,
  SenderAvatar,
} from "@ando/ui";

import {
  FINANCE_PROVIDER_COLORS,
  type FinanceConnection,
} from "@/features/workspace/hooks/useFinance";


// Capability → (Italian label, lucide glyph, hex tone). The hex from
// :data:`FINANCE_PROVIDER_COLORS` drives the avatar tint via an inline
// style (the avatar primitive accepts Tailwind classes; for one-off
// brand hexes we still resolve to slate-50 + custom fg via class +
// style merge below).
const CAPABILITY_VISUAL: Readonly<
  Record<
    FinanceConnection["capability"],
    { readonly label: MessageDescriptor; readonly icon: LucideIcon }
  >
> = {
  open_banking: { label: msg`Banca`, icon: Building2 },
  accounting: { label: msg`Contabilità`, icon: Receipt },
  crm: { label: msg`CRM`, icon: Briefcase },
  sdi_invoicing: { label: msg`SDI`, icon: Mail },
  cassetto_fiscale: { label: msg`Fisco`, icon: Shield },
};


/** Resolve a soft Tailwind tone pair for the avatar from the hex
 *  provider colour. We can't generate ``bg-[#xyz]`` at runtime (JIT
 *  purge) — fall back to a per-capability soft tone instead.
 *
 *  Pencil (Jul 2026): WARM-MUTED palette only (orange/amber/stone/emerald/
 *  rose) — the cold sky/violet tints are banned. Each capability still gets a
 *  distinct quiet tone so the eye differentiates sources at a glance. */
const CAPABILITY_TONE: Readonly<
  Record<FinanceConnection["capability"], { readonly bg: string; readonly fg: string }>
> = {
  open_banking: { bg: "bg-emerald-50", fg: "text-emerald-700" },
  accounting: { bg: "bg-amber-50", fg: "text-amber-700" },
  crm: { bg: "bg-stone-100", fg: "text-stone-600" },
  sdi_invoicing: { bg: "bg-ando-orange-soft", fg: "text-ando-orange" },
  cassetto_fiscale: { bg: "bg-rose-50", fg: "text-rose-700" },
};


/** Days between ``now`` and an ISO date (positive = future, negative = past). */
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((t - now.getTime()) / (24 * 3600 * 1000));
}


function formatConsentChip(
  days: number,
  i18n: I18n,
): { label: string; tone: "ok" | "warn" | "danger" } {
  if (days < 0) {
    const expiredDays = Math.abs(days);
    return { label: i18n._(msg`scaduto da ${expiredDays}g`), tone: "danger" };
  }
  if (days <= 7) return { label: i18n._(msg`${days}g residui`), tone: "danger" };
  if (days <= 30) return { label: i18n._(msg`${days}g residui`), tone: "warn" };
  if (days <= 90) return { label: i18n._(msg`${days}g residui`), tone: "ok" };
  // Beyond ~3 months we show the month count to reduce noise.
  const months = Math.round(days / 30);
  return { label: i18n._(msg`${months} mesi`), tone: "ok" };
}


export interface FinanceConnectionRowProps {
  readonly connection: FinanceConnection;
  readonly index?: number;
  /** Optional: surface a contextual action (open settings / renew). */
  readonly onClick?: (connection: FinanceConnection) => void;
}


export const FinanceConnectionRow: React.FC<FinanceConnectionRowProps> = ({
  connection,
  index = 0,
  onClick,
}) => {
  const { i18n } = useLingui();
  const visual = CAPABILITY_VISUAL[connection.capability];
  const tone = CAPABILITY_TONE[connection.capability];
  const Glyph = visual.icon;
  const visualLabel = i18n._(visual.label);
  // Provider hex tint (when known) — used as a thin dot in the eyebrow
  // so the user reads "this is the green bank brand" vs the orange one.
  const brandHex =
    FINANCE_PROVIDER_COLORS[connection.provider] ??
    FINANCE_PROVIDER_COLORS[connection.capability] ??
    null;

  const dueDays = daysUntil(connection.consent_expires_at);
  const consent = dueDays !== null ? formatConsentChip(dueDays, i18n) : null;

  return (
    <ListItemRow
      itemId={connection.id}
      index={index}
      avatar={
        <SenderAvatar
          toneBg={tone.bg}
          toneFg={tone.fg}
          size={28}
          icon={<Glyph size={14} strokeWidth={2} aria-hidden />}
          title={visualLabel}
          className="mt-0.5"
        />
      }
      eyebrow={
        <>
          <ListItemEyebrow>{visualLabel}</ListItemEyebrow>
          {brandHex ? (
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: brandHex }}
            />
          ) : null}
          <span className="truncate text-[10px] text-ink-3">
            {connection.provider}
          </span>
        </>
      }
      title={connection.account_label}
      trailing={
        consent ? (
          <span
            className={[
              "inline-flex items-center rounded-full border px-1.5 py-0.5 font-medium text-[10px] tabular-nums",
              consent.tone === "danger"
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : consent.tone === "warn"
                  ? "border-amber-200 bg-amber-50 text-amber-700"
                  : "border-hairline bg-white/60 text-ink-3",
            ].join(" ")}
            aria-label={i18n._(msg`Consenso · ${consent.label}`)}
          >
            {consent.label}
          </span>
        ) : null
      }
      pills={
        connection.needs_reauth ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 font-medium text-[10px] text-rose-700">
            {i18n._(msg`Da riautorizzare`)}
          </span>
        ) : null
      }
      onClick={() => onClick?.(connection)}
      highlighted={connection.needs_reauth}
    />
  );
};
FinanceConnectionRow.displayName = "FinanceConnectionRow";
